import { IInsuranceProduct, InsuranceType } from '@packlink/packlink-sdk';
import { Grid, GridChild, Icon, Next, Typography, Radio } from '@shipengine/giger';
import { IconNames } from '@shipengine/giger-theme';
import { Trans, useTranslation } from '@packlink/translation-provider';
import {
    getInsuranceCardStyles,
    getInsuranceInfoSectionStyles,
    getShipmentInsuranceSelectorStyles,
    getInsuranceSubtitleIconStyles,
    getInsuranceSubtitleTextStyles,
    getInsuranceTextStyles,
    getInsuranceTitleTextStyles,
    getRadioStyles,
} from './ShipmentInsuranceSelectorStyles';
import { HELP_CENTER_KEYS, useHelpCenter } from '@packlink/provider';
import {
    getInsuranceBadgeProps,
    InsuranceSelectBadge,
} from '@common/components/StepContent/DetailsStep/InsuranceSelect/InsuranceSelectBadge';
import { useMemo } from 'react';

interface ShipmentInsuranceSelectorProps {
    insurance: IInsuranceProduct;
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    totalPrice: string;
    isBulk?: boolean;
}

export const ShipmentInsuranceSelector = ({
    insurance,
    totalPrice,
    isBulk,
    onChange,
}: ShipmentInsuranceSelectorProps) => {
    const { t } = useTranslation();
    const getHelpCenterPage = useHelpCenter();

    const badgeProps = useMemo(() => {
        return !isBulk && getInsuranceBadgeProps(insurance, true, t);
    }, [insurance, isBulk, t]);

    return (
        <Grid noPadding css={getShipmentInsuranceSelectorStyles}>
            <GridChild colSpan={12}>
                <section css={getInsuranceInfoSectionStyles}>
                    <Typography variant="subtitle2" component="p" css={getInsuranceTitleTextStyles}>
                        <Icon name={IconNames.CARDBOARD_BOX_SHIELD} css={getInsuranceSubtitleIconStyles} />
                        {t('insurance-select.title.text')}
                    </Typography>
                    <Typography variant="body2" component="p" css={getInsuranceSubtitleTextStyles}>
                        <Trans
                            i18nKey="insurance-select.subtitle.text"
                            components={[
                                <Next.Link
                                    key="link"
                                    href={getHelpCenterPage(HELP_CENTER_KEYS.insurance)}
                                    isExternal
                                    target="_blank"
                                />,
                            ]}
                        />
                    </Typography>

                    {badgeProps && <InsuranceSelectBadge {...badgeProps} />}
                </section>
            </GridChild>

            <GridChild colSpan={12}>
                <div css={getInsuranceCardStyles(insurance.isSelected)}>
                    <Radio
                        name="hasInsurance"
                        onChange={onChange}
                        disabled={!insurance.isEnabled}
                        css={getRadioStyles}
                        value={InsuranceType.PACKLINK_INSURANCE}
                        checked={insurance.isSelected}
                    >
                        <div css={getInsuranceTextStyles}>
                            <span>{t('insurance-select.option.add-insurance')}</span>
                            <span>{totalPrice}</span>
                        </div>
                    </Radio>
                </div>
            </GridChild>

            <GridChild colSpan={12}>
                <div css={getInsuranceCardStyles(!insurance.isSelected)}>
                    <Radio
                        name="hasInsurance"
                        onChange={onChange}
                        disabled={!insurance.isEnabled}
                        css={getRadioStyles}
                        value={InsuranceType.NO_INSURANCE}
                        checked={!insurance.isSelected}
                    >
                        {t('insurance-select.option.no-insurance')}
                    </Radio>
                </div>
            </GridChild>
        </Grid>
    );
};
